/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: role_permissions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the role_permissions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the role_permissions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, role_id, permission_id, created_at, created_by, trig_role_name, trig_role_description, trig_permission_name]
 * Searchable fields: []
 * Filterable fields: [id, role_id, permission_id]
 */

import { httpClient } from "../http-client.js";
import type { UnifiedQueryOptions } from "../../../../../config/search/global-search-config.js";
import type {
  RolePermissionsRow,
  RolePermissionsInsert,
  RolePermissionsUpdate,
} from "../../types/generated_schemas/generated-role_permissions-schema.js";

export interface RolePermissionsQueryOptions extends UnifiedQueryOptions {}

export interface RolePermissionsResponse {
  data: RolePermissionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for role_permissions table operations
 *
 * @description Provides type-safe access to role_permissions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class RolePermissionsClient {
  private readonly basePath = "/role-permissions";

  /**
   * Get all role_permissions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of role_permissions records
   */
  async getAll(
    options?: RolePermissionsQueryOptions,
  ): Promise<RolePermissionsRow[]> {
    const params = new URLSearchParams();

    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());
    if (options?.sortBy) params.set("sortBy", options.sortBy);
    if (options?.sortOrder) params.set("sortOrder", options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await httpClient.get<RolePermissionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single role_permissions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the role_permissions record
   */
  async getById(id: string): Promise<RolePermissionsRow> {
    const response = await httpClient.get<{
      data: RolePermissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new role_permissions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created role_permissions record
   */
  async create(data: RolePermissionsInsert): Promise<RolePermissionsRow> {
    const response = await httpClient.post<{
      data: RolePermissionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing role_permissions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated role_permissions record
   */
  async update(
    id: string,
    data: RolePermissionsUpdate,
  ): Promise<RolePermissionsRow> {
    const response = await httpClient.put<{
      data: RolePermissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a role_permissions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple role_permissions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created role_permissions records
   */
  async createMany(
    data: RolePermissionsInsert[],
  ): Promise<RolePermissionsRow[]> {
    const response = await httpClient.post<{
      data: RolePermissionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple role_permissions records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search role_permissions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: RolePermissionsQueryOptions,
  ): Promise<RolePermissionsResponse> {
    throw new Error(
      "Search operations are not available for role_permissions - no searchable fields configured",
    );
  }

  /**
   * Get count of role_permissions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/count?${queryString}`
      : `${this.basePath}/count`;

    const response = await httpClient.get<{ data: number; success: boolean }>(
      url,
    );
    return response.data;
  }

  /**
   * Check if a role_permissions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: "role_permissions",
      publicFields: [
        "id",
        "role_id",
        "permission_id",
        "created_at",
        "created_by",
        "trig_role_name",
        "trig_role_description",
        "trig_permission_name",
      ],
      searchableFields: [],
      filterableFields: ["id", "role_id", "permission_id"],
      sortableFields: ["created_at"],
      readonlyFields: ["id", "created_at"],
      requiredFields: ["id"],
    };
  }
}

// Export singleton instance for convenience
export const rolePermissionsClient = new RolePermissionsClient();
